import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Trophy, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import Logo from '@/components/Logo';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ClubLeaderboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState('avg_elo');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/clubs/leaderboard`, { withCredentials: true });
      setLeaderboard(response.data || []);
    } catch (error) {
      toast.error(t('failedToLoadLeaderboard'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sorted = [...leaderboard].sort((a, b) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    return (a[sortField] - b[sortField]) * mul;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === 'desc'
      ? <ChevronDown className="w-3 h-3 text-[#007AFF]" />
      : <ChevronUp className="w-3 h-3 text-[#007AFF]" />;
  };

  const getRankStyle = (index) => {
    if (index === 0) return 'text-yellow-400';
    if (index === 1) return 'text-gray-300';
    if (index === 2) return 'text-amber-600';
    return 'text-gray-500';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#007AFF]"></div>
          <p className="mt-4 text-white">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-white hover:bg-white/10"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backToDashboard')}
          </Button>
          <Logo size="lg" />
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t('clubLeaderboard')}
              </h1>
              <p className="text-gray-400 text-sm">{t('clubLeaderboardDesc')}</p>
            </div>
          </div>
        </div>

        {leaderboard.length === 0 ? (
          <Card className="bg-[#111111] border-[#27272A]">
            <CardContent className="py-16 text-center">
              <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">{t('noClubsYet')}</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#111111] border-[#27272A]" data-testid="club-leaderboard-card">
            <CardContent className="p-0">
              {/* Table Header */}
              <div className="hidden sm:grid grid-cols-[50px_1fr_100px_100px_100px_90px_80px] gap-2 px-4 py-3 border-b border-[#27272A] text-xs uppercase tracking-wider text-gray-500 font-semibold">
                <div>#</div>
                <div>{t('club')}</div>
                <button
                  onClick={() => handleSort('avg_elo')}
                  className="flex items-center gap-1 hover:text-white transition-colors"
                  data-testid="sort-avg-elo"
                >
                  {t('avgElo')} <SortIcon field="avg_elo" />
                </button>
                <button
                  onClick={() => handleSort('total_matches')}
                  className="flex items-center gap-1 hover:text-white transition-colors"
                  data-testid="sort-total-matches"
                >
                  {t('matches')} <SortIcon field="total_matches" />
                </button>
                <button
                  onClick={() => handleSort('total_wins')}
                  className="flex items-center gap-1 hover:text-white transition-colors"
                  data-testid="sort-total-wins"
                >
                  {t('wins')} <SortIcon field="total_wins" />
                </button>
                <button
                  onClick={() => handleSort('win_rate')}
                  className="flex items-center gap-1 hover:text-white transition-colors"
                  data-testid="sort-win-rate"
                >
                  {t('winRate')} <SortIcon field="win_rate" />
                </button>
                <button
                  onClick={() => handleSort('member_count')}
                  className="flex items-center gap-1 hover:text-white transition-colors"
                  data-testid="sort-members"
                >
                  {t('members')} <SortIcon field="member_count" />
                </button>
              </div>

              {/* Table Rows */}
              {sorted.map((club, index) => (
                <div
                  key={club.club_id}
                  className={`grid grid-cols-1 sm:grid-cols-[50px_1fr_100px_100px_100px_90px_80px] gap-2 px-4 py-4 items-center border-b border-[#27272A]/50 last:border-0 transition-colors hover:bg-white/[0.02] ${index === 0 ? 'bg-yellow-500/[0.03]' : ''}`}
                  data-testid={`club-row-${club.club_id}`}
                >
                  {/* Rank */}
                  <div className={`text-xl font-bold ${getRankStyle(index)} hidden sm:block`}>
                    {index + 1}
                  </div>

                  {/* Club Name + Logo */}
                  <div className="flex items-center gap-3">
                    <span className={`text-lg font-bold ${getRankStyle(index)} sm:hidden mr-1`}>
                      #{index + 1}
                    </span>
                    {club.logo_url ? (
                      <img src={club.logo_url} alt={club.name} className="w-10 h-10 object-contain rounded-lg flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 bg-[#27272A] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-gray-500" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-white font-bold truncate">{club.name}</p>
                      <p className="text-xs text-gray-500 sm:hidden">
                        {club.member_count} {t('members')} &middot; {t('avgElo')}: {club.avg_elo}
                      </p>
                    </div>
                  </div>

                  {/* Avg ELO */}
                  <div className="hidden sm:block">
                    <span className="text-white font-bold text-lg">{club.avg_elo}</span>
                  </div>

                  {/* Total Matches */}
                  <div className="hidden sm:block text-gray-300">
                    {club.total_matches}
                  </div>

                  {/* Total Wins */}
                  <div className="hidden sm:block text-gray-300">
                    <span className="text-green-400">{club.total_wins}</span>
                    <span className="text-gray-600 mx-1">/</span>
                    <span className="text-red-400">{club.total_losses}</span>
                  </div>

                  {/* Win Rate */}
                  <div className="hidden sm:block">
                    <span className={`font-medium ${club.win_rate >= 50 ? 'text-green-400' : 'text-gray-400'}`}>
                      {club.win_rate}%
                    </span>
                  </div>

                  {/* Members */}
                  <div className="hidden sm:block text-gray-400">
                    {club.member_count}
                  </div>

                  {/* Mobile stats row */}
                  <div className="flex gap-4 text-xs text-gray-400 sm:hidden col-span-full mt-1">
                    <span>{t('matches')}: <span className="text-white">{club.total_matches}</span></span>
                    <span>{t('wins')}: <span className="text-green-400">{club.total_wins}</span></span>
                    <span>{t('winRate')}: <span className={club.win_rate >= 50 ? 'text-green-400' : 'text-gray-400'}>{club.win_rate}%</span></span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default ClubLeaderboard;
